import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import useRegionSelector from '../../../hooks/useRegionSelector';
import { useLocation } from '@reach/router';

import './styles.scss';
import Spinner from '../Spinner/Spinner';

const RegionSelector = ({ onMobileClick, noActions = false }) => {
  const location = useLocation();
  const dropdownRef = useRef(null);

  const { region, updateRegion } = useRegionSelector();
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [showInitialPopUp, setShowInitialPopup] = useState(location.pathname === '/');
  const [isLoading, setIsLoading] = useState(true);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const isInHomepage = window.location;
    const savedRegion = localStorage.getItem('region');
    const sessionClosedPopup = sessionStorage.getItem('regionPopupClosed');
    if (!savedRegion && !sessionClosedPopup && location.pathname === '/') {
      document.body.classList.add('overflow-y-hidden');
      setShowInitialPopup(true);
    } else {
      setShowInitialPopup(false);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSelectorOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleOnChangeRegion = (region) => {
    setSelectorOpen(false);

    if (onMobileClick && window.innerWidth <= 992) {
      onMobileClick();
    }

    updateRegion(region);
    document.body.style.overflow = '';
  };

  const handleOnOpenModal = () => {
    if (onMobileClick && window.innerWidth <= 992) {
      onMobileClick();
    }
    if (noActions) return;

    if (selectorOpen) {
      setSelectorOpen(false);
    } else {
      setSelectorOpen(true);
    }
  };

  const regionMapped = {
    EU: 'Europe',
    UK: 'UK',
    GLOBAL: 'Global',
  };

  const regionIconMap = {
    EU: (
      <svg
        className="region_icon"
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="26"
        viewBox="0 0 25 26"
        fill="none"
      >
        <path
          d="M25 12.5558C25 19.4902 19.4036 25.1116 12.5 25.1116C5.59644 25.1116 0 19.4902 0 12.5558C0 5.62142 5.59644 0 12.5 0C19.4036 0 25 5.62142 25 12.5558Z"
          fill="#2980B9"
        />
        <path
          d="M12.4993 4.89539L12.7243 5.58524H13.4504L12.8631 6.01244L13.0866 6.7023L12.4993 6.27661L11.9121 6.7023L12.1356 6.01244L11.5483 5.58524H12.2759L12.4993 4.89539Z"
          fill="#F1C40F"
        />
        <path
          d="M9.10822 5.7981L9.33316 6.48946L10.0592 6.48826L9.47204 6.91546L9.69696 7.60683L9.10975 7.18114L8.52103 7.60834L8.74595 6.91697L8.15723 6.48977H8.88481L9.10822 5.7984"
          fill="#F1C40F"
        />
        <path
          d="M6.6287 8.27527L6.85362 8.96513H7.58122L6.99249 9.39233L7.21892 10.0837L6.63021 9.6565L6.043 10.0852L6.26641 9.39384L5.6792 8.96664H6.40529L6.6287 8.27527Z"
          fill="#F1C40F"
        />
        <path
          d="M15.8816 5.80872L16.1081 6.50009L16.8341 6.49887L16.2484 6.9276L16.4734 7.61896L15.8846 7.19177L15.2974 7.61896L15.5224 6.92909L14.9321 6.50189L15.6597 6.5007L15.8816 5.80933"
          fill="#F1C40F"
        />
        <path
          d="M19.2126 8.91378L18.6254 9.34098L18.8503 10.0323L18.2631 9.60515L17.6744 10.0323L17.8993 9.34098L17.3105 8.91529H18.0366L18.2616 8.22241L18.485 8.91378H19.2126Z"
          fill="#F1C40F"
        />
        <path
          d="M20.1487 12.3147L19.5584 12.7389L19.7803 13.4303L19.1946 13.0016L18.6044 13.4258L18.8308 12.7359L18.2451 12.3072L18.9712 12.3096L19.1992 11.6198L19.4211 12.3111L20.1487 12.3135"
          fill="#F1C40F"
        />
        <path
          d="M18.9064 16.6955L18.3192 16.2698L17.7305 16.697L17.9554 16.0056L17.3667 15.5799L18.0928 15.5787L18.3177 14.8873L18.5426 15.5787L19.2702 15.5775L18.6815 16.0047L18.9064 16.6961"
          fill="#F1C40F"
        />
        <path
          d="M11.9718 20.1039L12.1937 19.4126L11.6064 18.9869L12.3325 18.9857L12.5559 18.2943L12.7824 18.9842L13.507 18.983L12.9213 19.4117L13.1477 20.1015L12.559 19.6774L11.9718 20.1046"
          fill="#F1C40F"
        />
        <path
          d="M8.5777 19.2058L8.80111 18.516L8.2124 18.0888H8.94L9.16341 17.3959L9.38833 18.0872L10.1144 18.086L9.5272 18.5132L9.75363 19.2046L9.16492 18.7789L8.5777 19.2062"
          fill="#F1C40F"
        />
        <path
          d="M16.4268 19.1907L15.8396 18.7605L15.2494 19.1862L15.4773 18.4948L14.8916 18.0661L15.6177 18.0685L15.8441 17.3787L16.066 18.07L16.7936 18.0712L16.2034 18.4969L16.4268 19.1897"
          fill="#F1C40F"
        />
        <path
          d="M4.85156 12.3284L5.57765 12.3272L5.80106 11.6343L6.02749 12.3241L6.75358 12.3229L6.16787 12.7532L6.3943 13.443L5.80559 13.0173L5.21687 13.446L5.44028 12.7547L4.85156 12.3284Z"
          fill="#F1C40F"
        />
        <path
          d="M6.09528 16.6924L6.31869 16.0011L5.72998 15.5754L6.45758 15.5742L6.6825 14.8828L6.90742 15.5742H7.6335L7.0463 16.0014L7.27122 16.6912L6.6825 16.2656L6.09528 16.6928"
          fill="#F1C40F"
        />
      </svg>
    ),
    UK: (
      <svg
        className="region_icon"
        xmlns="http://www.w3.org/2000/svg"
        width="36"
        height="36"
        viewBox="0 0 36 36"
        fill="none"
      >
        <mask id="mask0_1657_5090" maskUnits="userSpaceOnUse" x="1" y="1" width="34" height="34">
          <path
            d="M34.8002 18C34.8002 22.4556 33.0302 26.7288 29.8796 29.8794C26.729 33.03 22.4558 34.8 18.0002 34.8C13.5446 34.8 9.27141 33.03 6.1208 29.8794C2.97019 26.7288 1.2002 22.4556 1.2002 18C1.2002 13.5444 2.97019 9.27123 6.1208 6.12062C9.27141 2.97001 13.5446 1.20001 18.0002 1.20001C22.4558 1.20001 26.729 2.97001 29.8796 6.12062C33.0302 9.27123 34.8002 13.5444 34.8002 18Z"
            fill="white"
          />
        </mask>
        <g mask="url(#mask0_1657_5090)">
          <path d="M0 35.9996H36V0H0V35.9996Z" fill="#356BA0" />
          <path d="M36 12.6018H21.6V0H14.4V12.6018H0V23.4021H14.4V35.9996H21.6V23.4021H36V12.6018Z" fill="white" />
          <path d="M36 31.8661L4.13092 0H0V4.16935L31.8375 35.9996H36V31.8661Z" fill="white" />
          <path d="M0 31.8661L31.8685 0H36V4.16935L4.16247 35.9996H0V31.8661Z" fill="white" />
          <path
            d="M36 14.8504H20.1002V0H15.8997V14.8504H0V21.1492H15.8997V35.9996H20.1002V21.1492H36V14.8504Z"
            fill="#CA4638"
          />
          <path d="M35.9998 35.8974V33.0517L26.3241 23.4021H23.4736L35.9998 35.8974Z" fill="#CA4638" />
          <path d="M36.0001 0H33.1539L21.6001 11.527V12.6018H23.3742L36.0001 0.00324236" fill="#CA4638" />
          <path d="M12.5305 23.4021L0 35.9007V36H2.74996L14.4 24.3769V23.4025H12.5305" fill="#CA4638" />
          <path d="M0 0.0244141V2.86792L9.756 12.6018H12.6063L0 0.0244141Z" fill="#CA4638" />
        </g>
      </svg>
    ),
    GLOBAL: (
      <svg
        className="region_icon"
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="26"
        viewBox="0 0 25 26"
        fill="none"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.00978649 13.1009C-0.260925 6.20612 5.11791 0.389987 12.0106 0.121359C18.9055 -0.149352 24.7216 5.22948 24.9902 12.1222C25.2609 19.017 19.8821 24.8332 12.9894 25.1018C6.09455 25.3725 0.278416 19.9937 0.00978649 13.1009ZM12.9602 24.348C13.7828 24.3084 14.5824 24.1605 15.3508 23.919C15.5007 23.8524 15.6382 23.7795 15.7569 23.7003C13.6911 20.9058 11.1381 22.3801 8.9162 23.1131C9.5659 23.9315 11.0194 24.3167 12.4604 24.3646C12.6291 24.3646 12.7936 24.3563 12.9602 24.348ZM18.7097 16.1204C19.0533 15.7018 20.1799 15.3832 20.2069 14.8647C20.2194 14.6607 20.284 14.3566 20.309 14.1609C20.3277 13.9776 19.1095 13.4216 18.9117 13.255C18.3245 12.7761 18.1433 11.6099 17.4624 11.0935C17.4499 11.0873 17.4624 11.0623 17.4624 11.0456C16.9251 10.979 14.7802 10.6146 14.4491 10.0835C14.3887 9.98984 14.3867 9.89405 14.4616 9.78784C14.1826 9.67956 14.0243 9.59418 13.714 9.4859C11.8336 8.82994 10.2843 10.1294 9.51593 11.3455C8.09365 13.5945 12.4209 14.0214 12.9623 14.4128C13.5287 14.8189 13.8057 17.6093 13.8598 18.5818C14.1534 23.7961 18.212 16.716 18.7097 16.1204ZM4.47861 14.3566C4.42863 14.0713 3.2375 13.33 3.06258 13.0447C2.43994 12.0264 1.35918 10.069 1.82772 8.93614C1.17593 9.6629 0.769861 11.1997 0.75112 12.8365C0.75112 12.8989 0.753202 12.9593 0.755284 13.0197C0.809427 14.315 1.06764 15.554 1.50078 16.7181C1.88186 17.4261 2.40663 17.9758 3.11048 18.232C2.09427 17.1783 4.87427 16.4744 4.47861 14.3566ZM24.2301 11.8661C24.1177 10.0752 23.6075 8.39888 22.7891 6.92455C21.6501 5.46895 20.0029 3.76139 19.3677 3.73432C18.6785 3.70308 17.958 4.38403 17.581 4.36945C17.3103 4.35695 17.4853 3.67601 16.8522 3.60104C16.5773 3.56356 14.951 4.71096 14.824 4.90046C14.622 5.19199 14.9656 5.61889 14.9656 6.00621C14.9656 6.04578 14.8739 6.2436 14.8739 6.2436C13.8223 6.68299 11.7816 7.10988 12.1585 8.23854C12.1814 8.29685 12.2022 8.35307 12.2335 8.41554C12.5271 8.30934 13.3767 7.81998 13.612 7.83664C14.497 7.90327 16.2296 8.73623 16.9043 9.18603C17.7581 9.75036 19.0387 12.7074 20.7734 12.168C21.1315 12.0597 21.194 11.6662 21.0149 11.3517C20.8088 11.004 21.4147 11.0248 21.498 11.1102C21.7042 11.3205 23.2597 12.6282 23.5554 12.8052C23.9615 13.051 24.1635 12.5824 24.2301 11.8661ZM4.43904 5.86044C5.87798 5.13993 5.78844 5.96873 6.70261 5.75841C7.20447 5.63971 7.15032 5.04623 7.24403 4.66515C7.36481 4.18411 9.53467 3.13875 10.049 2.90136C10.3406 2.76392 10.6238 2.61399 10.8861 2.53694C11.9128 2.22666 8.72253 4.40485 8.67256 4.71512C8.608 5.08371 8.89746 5.20032 9.2119 5.13369C10.0969 4.9546 13.0664 3.66143 14.0326 3.26161C14.2117 2.66813 13.7203 2.40158 13.2476 2.48696C12.9748 2.53069 12.8228 1.71023 12.9206 1.50615C12.4604 1.19171 11.9378 1.00221 11.3797 0.906421C8.15612 1.21462 5.32823 2.81598 3.40201 5.16909C3.55611 5.37733 3.76018 5.57307 3.69563 5.80214C3.7935 5.81047 4.35367 5.90626 4.43904 5.86044ZM3.69563 5.80214C3.6394 5.79797 3.73519 5.87502 3.69563 5.80214V5.80214Z"
          fill="#011C25"
        />
      </svg>
    ),
  };

  const handleOnClosePopup = () => {
    setShowInitialPopup(false);
    document.body.classList.remove('overflow-y-hidden');
  };

  if (isLoading) {
    return (
      <div className="full-loading">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div ref={dropdownRef} className={`region-selector2`} onClick={handleOnOpenModal}>
        <div className="wrapper">
          <div className="desktop-icon">{regionIconMap[region]}</div>

          <span className="current-region">{regionMapped[region]}</span>
          <div className="mobile-options">
            {region !== 'UK' && (
              <div className="item" onClick={() => handleOnChangeRegion('UK')}>
                UK
              </div>
            )}

            {region !== 'EU' && (
              <div className="item" onClick={() => handleOnChangeRegion('EU')}>
                EUROPE
              </div>
            )}

            {region !== 'GLOBAL' && (
              <div className="item" onClick={() => handleOnChangeRegion('GLOBAL')}>
                GLOBAL
              </div>
            )}
          </div>
        </div>

        <svg
          className="default hide-mobile arrow"
          xmlns="http://www.w3.org/2000/svg"
          width="9"
          height="8"
          viewBox="0 0 9 8"
          fill="none"
        >
          <path d="M4.5 7.5L0.602886 0.750001L8.39711 0.75L4.5 7.5Z" fill="#011C25" />
        </svg>

        {selectorOpen && (
          <div className="modal-region-selector">
            {region !== 'UK' && (
              <div className="item" onClick={() => handleOnChangeRegion('UK')}>
                {regionIconMap['UK']}
                UK
              </div>
            )}

            {region !== 'EU' && (
              <div className="item" onClick={() => handleOnChangeRegion('EU')}>
                {regionIconMap['EU']}
                EUROPE
              </div>
            )}

            {region !== 'GLOBAL' && (
              <div className="item" onClick={() => handleOnChangeRegion('GLOBAL')}>
                {regionIconMap['GLOBAL']}
                GLOBAL
              </div>
            )}
          </div>
        )}
      </div>

      {/* Initial popup for those users with no region set */}
      {showInitialPopUp && (
        <section className="ui-region-popup2">
          <div className="header">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="285"
              height="42"
              viewBox="0 0 285 42"
              fill="none"
              className="region_icon"
            >
              <path
                d="M5.01479 29.2652V40.6997H0V10.5805H4.63552V13.6463C6.18069 11.271 8.63892 10.0834 12.0102 10.0834C15.1567 10.0834 17.629 11.1605 19.4411 13.3149C21.2531 15.4692 22.1662 18.1483 22.1662 21.3522C22.1662 24.5561 21.2531 27.2214 19.4411 29.3619C17.629 31.5024 15.1427 32.5796 12.0102 32.5796C8.87772 32.5796 6.47568 31.4748 5.01479 29.2652ZM4.84622 21.3522C4.84622 23.3408 5.39406 24.9842 6.50377 26.2823C7.61349 27.5804 9.10247 28.2295 10.9988 28.2295C12.8952 28.2295 14.342 27.5804 15.4658 26.2685C16.5895 24.9565 17.1514 23.327 17.1514 21.366C17.1514 19.405 16.5895 17.6788 15.4658 16.353C14.342 15.0273 12.853 14.3644 10.9988 14.3644C9.14461 14.3644 7.61349 15.0273 6.50377 16.3254C5.39406 17.6373 4.84622 19.3221 4.84622 21.366V21.3522Z"
                fill="#1EB48F"
              />
              <path
                d="M35.23 32.5796C31.8587 32.5796 29.1195 31.4886 27.0405 29.3066C24.9475 27.1247 23.894 24.4732 23.894 21.3522C23.894 18.2312 24.9475 15.5659 27.0405 13.3701C29.1335 11.1744 31.8587 10.0834 35.23 10.0834C38.6012 10.0834 41.3825 11.1744 43.4756 13.3701C45.5686 15.5659 46.608 18.2174 46.608 21.3522C46.608 24.487 45.5545 27.1247 43.4756 29.3066C41.3825 31.4886 38.6293 32.5796 35.23 32.5796ZM28.9088 21.3522C28.9088 23.396 29.4988 25.0532 30.7068 26.3237C31.9008 27.5942 33.4038 28.2295 35.23 28.2295C37.0561 28.2295 38.6012 27.5942 39.7952 26.3237C40.9892 25.0532 41.5933 23.396 41.5933 21.3522C41.5933 19.3083 40.9892 17.6097 39.7952 16.3392C38.6012 15.0687 37.0842 14.4335 35.23 14.4335C33.3757 14.4335 31.8868 15.0687 30.7068 16.3392C29.5128 17.6097 28.9088 19.2807 28.9088 21.3522Z"
                fill="#1EB48F"
              />
              <path
                d="M47.8301 26.0751L52.5499 25.3294C52.6342 26.2961 53.0837 27.0694 53.8844 27.6495C54.6851 28.2295 55.8088 28.5195 57.2838 28.5195C58.5761 28.5195 59.5734 28.2985 60.2758 27.8566C60.9781 27.4147 61.3293 26.8209 61.3293 26.0751C61.3293 25.2466 60.8938 24.6251 60.037 24.2246C59.1801 23.8241 58.1547 23.4927 56.9607 23.2441C55.7667 22.9956 54.5586 22.7332 53.3365 22.4846C52.1145 22.236 51.075 21.6284 50.2181 20.6893C49.3612 19.7503 48.9258 18.4659 48.9258 16.8364C48.9258 14.9582 49.7124 13.3701 51.3138 12.0582C52.9011 10.7463 54.9941 10.0972 57.6068 10.0972C60.2196 10.0972 62.13 10.7186 63.5066 11.9615C64.8832 13.2044 65.7401 14.6682 66.0772 16.353L61.3995 17.1816C61.2872 16.3254 60.8938 15.6073 60.2336 14.9997C59.5734 14.4059 58.6604 14.1158 57.4804 14.1158C56.3005 14.1158 55.3453 14.3368 54.6991 14.7787C54.0529 15.2206 53.7299 15.8283 53.7299 16.6016C53.7299 17.3197 54.1653 17.8721 55.0362 18.245C55.9072 18.6178 56.9326 18.9355 58.1266 19.1979C59.3206 19.4602 60.5286 19.7364 61.7507 19.9988C62.9728 20.275 64.0123 20.9103 64.8551 21.9046C65.6979 22.8989 66.1193 24.2246 66.1193 25.868C66.1193 27.8014 65.3187 29.4033 63.7313 30.6738C62.144 31.9443 59.8825 32.5796 56.9747 32.5796C54.067 32.5796 52.0161 31.9719 50.5271 30.7705C49.0382 29.569 48.1391 28.0085 47.8301 26.0751Z"
                fill="#1EB48F"
              />
              <path
                d="M71.7522 6.97618C70.8251 6.97618 70.0665 6.68617 69.4766 6.11997C68.8866 5.55377 68.5916 4.83566 68.5916 3.93802C68.5916 3.04038 68.8866 2.28084 69.4766 1.70083C70.0665 1.12082 70.8251 0.830811 71.7522 0.830811C72.6793 0.830811 73.3957 1.12082 73.9857 1.70083C74.5756 2.28084 74.8706 3.02657 74.8706 3.93802C74.8706 4.84947 74.5756 5.55377 73.9857 6.11997C73.3957 6.68617 72.6512 6.97618 71.7522 6.97618ZM69.2237 32.0824V10.5805H74.2385V32.0824H69.2237Z"
                fill="#1EB48F"
              />
              <path
                d="M86.6701 5.85758H91.3899V14.3092H100.029V18.9493H91.3899V27.3595H86.6701V18.9493H78.0312V14.3092H86.6701V5.85758Z"
                fill="#1EB48F"
              />
              <path
                d="M106.35 6.97618C105.423 6.97618 104.664 6.68617 104.074 6.11997C103.484 5.55377 103.189 4.83566 103.189 3.93802C103.189 3.04038 103.484 2.28084 104.074 1.70083C104.664 1.12082 105.423 0.830811 106.35 0.830811C107.277 0.830811 107.993 1.12082 108.583 1.70083C109.173 2.28084 109.468 3.02657 109.468 3.93802C109.468 4.84947 109.173 5.55377 108.583 6.11997C107.993 6.68617 107.249 6.97618 106.35 6.97618ZM103.822 32.0824V10.5805H108.836V32.0824H103.822Z"
                fill="#1EB48F"
              />
              <path
                d="M121.9 26.2409L127.954 10.5805H133.138L124.204 32.0824H119.484L110.592 10.5805H115.902L121.9 26.2409Z"
                fill="#1EB48F"
              />
              <path
                d="M148.969 25.0394L153.478 26.1994C152.691 28.1604 151.413 29.7071 149.671 30.8671C147.915 32.0134 145.864 32.5934 143.532 32.5934C140.274 32.5934 137.633 31.53 135.638 29.4033C133.629 27.2766 132.618 24.5837 132.618 21.3246C132.618 18.0655 133.629 15.483 135.652 13.3287C137.675 11.1744 140.288 10.0972 143.49 10.0972C146.693 10.0972 149.278 11.1467 151.272 13.2596C153.253 15.3725 154.25 18.0378 154.25 21.2279L154.208 22.7608H137.562C137.759 24.4732 138.391 25.8404 139.459 26.8485C140.526 27.8566 141.931 28.3538 143.673 28.3538C146.258 28.3538 148.028 27.249 148.983 25.0394H148.969ZM143.49 14.102C141.917 14.102 140.611 14.5578 139.571 15.4692C138.532 16.3807 137.886 17.5959 137.633 19.115H149.264C149.039 17.5959 148.421 16.3807 147.409 15.4692C146.398 14.5578 145.092 14.102 143.49 14.102Z"
                fill="#1EB48F"
              />
              <path
                d="M176.29 19.7364V32.0824H171.275V19.1979C171.275 15.9387 169.899 14.3092 167.145 14.3092C165.656 14.3092 164.462 14.7787 163.578 15.7178C162.693 16.6569 162.257 17.9412 162.257 19.5707V32.0824H157.242V10.5805H161.878V13.4392C162.412 12.4725 163.226 11.6715 164.322 11.0363C165.418 10.401 166.78 10.0834 168.41 10.0834C171.444 10.0834 173.635 11.2158 174.984 13.4806C176.838 11.2158 179.31 10.0834 182.401 10.0834C184.957 10.0834 186.924 10.8705 188.3 12.4172C189.677 13.9778 190.365 16.0354 190.365 18.5764V32.0824H185.35V19.1979C185.35 15.9387 183.974 14.3092 181.221 14.3092C179.704 14.3092 178.495 14.7787 177.611 15.7454C176.726 16.7121 176.276 18.0378 176.276 19.7364H176.29Z"
                fill="#1EB48F"
              />
              <path
                d="M204.567 32.5796C201.195 32.5796 198.456 31.4886 196.377 29.3066C194.284 27.1247 193.231 24.4732 193.231 21.3522C193.231 18.2312 194.284 15.5659 196.377 13.3701C198.47 11.1744 201.195 10.0834 204.567 10.0834C207.938 10.0834 210.719 11.1744 212.812 13.3701C214.905 15.5659 215.945 18.2174 215.945 21.3522C215.945 24.487 214.891 27.1247 212.812 29.3066C210.719 31.4886 207.966 32.5796 204.567 32.5796ZM198.246 21.3522C198.246 23.396 198.836 25.0532 200.044 26.3237C201.238 27.5942 202.741 28.2295 204.567 28.2295C206.393 28.2295 207.938 27.5942 209.132 26.3237C210.326 25.0532 210.93 23.396 210.93 21.3522C210.93 19.3083 210.326 17.6097 209.132 16.3392C207.938 15.0687 206.421 14.4335 204.567 14.4335C202.713 14.4335 201.224 15.0687 200.044 16.3392C198.85 17.6097 198.246 19.2807 198.246 21.3522Z"
                fill="#1EB48F"
              />
              <path
                d="M223.6 10.5805V13.4392C224.106 12.5001 224.949 11.7129 226.115 11.0639C227.281 10.4148 228.685 10.0834 230.343 10.0834C232.928 10.0834 234.922 10.8567 236.355 12.4034C237.774 13.9501 238.476 16.0078 238.476 18.5764V32.0824H233.461V19.1979C233.461 17.6788 233.096 16.4911 232.38 15.6211C231.663 14.7511 230.568 14.3092 229.107 14.3092C227.506 14.3092 226.241 14.7787 225.342 15.7178C224.429 16.6569 223.98 17.955 223.98 19.6122V32.0824H218.965V10.5805H223.6Z"
                fill="#1EB48F"
              />
              <path
                d="M257.693 25.0394L262.202 26.1994C261.415 28.1604 260.137 29.7071 258.395 30.8671C256.639 32.0134 254.588 32.5934 252.256 32.5934C248.998 32.5934 246.357 31.53 244.362 29.4033C242.353 27.2766 241.342 24.5837 241.342 21.3246C241.342 18.0655 242.353 15.483 244.376 13.3287C246.399 11.1744 249.012 10.0972 252.214 10.0972C255.417 10.0972 258.002 11.1467 259.996 13.2596C261.977 15.3725 262.974 18.0378 262.974 21.2279L262.932 22.7608H246.286C246.483 24.4732 247.115 25.8404 248.183 26.8485C249.25 27.8566 250.655 28.3538 252.397 28.3538C254.982 28.3538 256.751 27.249 257.707 25.0394H257.693ZM252.214 14.102C250.641 14.102 249.335 14.5578 248.295 15.4692C247.256 16.3807 246.61 17.5959 246.357 19.115H257.988C257.763 17.5959 257.145 16.3807 256.133 15.4692C255.122 14.5578 253.816 14.102 252.214 14.102Z"
                fill="#1EB48F"
              />
              <path
                d="M271.262 31.8891L262.455 10.5805H267.806L273.65 25.9094L279.775 10.5805H285L275.308 33.9882C274.212 36.6397 272.976 38.4902 271.599 39.5673C270.223 40.6307 268.509 41.1693 266.458 41.1693L265.025 41.0864V36.6949C265.418 36.7225 265.84 36.7363 266.289 36.7363C267.245 36.7363 268.087 36.4877 268.804 35.9906C269.52 35.4934 270.18 34.5268 270.77 33.1043L271.276 31.9029L271.262 31.8891Z"
                fill="#1EB48F"
              />
            </svg>
          </div>
          <button
            className="close"
            onClick={() => {
              setShowInitialPopup(false);
              sessionStorage.setItem('regionPopupClosed', 'true');
              document.body.classList.remove('overflow-y-hidden');
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="38"
              height="38"
              viewBox="0 0 38 38"
              fill="none"
              className="region_icon"
            >
              <g clipPath="url(#clip0_494_4486)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.9999 21.2389L27.9568 30.1958C28.2555 30.4842 28.6554 30.6438 29.0706 30.6402C29.4857 30.6366 29.8828 30.4701 30.1764 30.1765C30.4699 29.883 30.6365 29.4859 30.6401 29.0707C30.6437 28.6556 30.4841 28.2556 30.1957 27.957L21.2388 19.0001L30.1957 10.0432C30.4841 9.74454 30.6437 9.34458 30.6401 8.92944C30.6365 8.51429 30.4699 8.11717 30.1764 7.82361C29.8828 7.53005 29.4857 7.36353 29.0706 7.35992C28.6554 7.35632 28.2555 7.51591 27.9568 7.80432L18.9999 16.7612L10.043 7.80432C9.74304 7.52304 9.34541 7.36949 8.93425 7.37616C8.52308 7.38284 8.13064 7.54922 7.83997 7.8401C7.5493 8.13098 7.3832 8.52353 7.37681 8.9347C7.37043 9.34587 7.52426 9.74339 7.80576 10.0432L16.7611 19.0001L7.80417 27.957C7.65295 28.103 7.53233 28.2778 7.44935 28.4709C7.36637 28.6641 7.32269 28.8719 7.32086 29.0821C7.31903 29.2923 7.35909 29.5008 7.43871 29.6954C7.51832 29.89 7.63588 30.0668 7.78455 30.2154C7.93321 30.3641 8.10999 30.4817 8.30458 30.5613C8.49917 30.6409 8.70766 30.681 8.91789 30.6791C9.12813 30.6773 9.33589 30.6336 9.52906 30.5506C9.72224 30.4677 9.89695 30.347 10.043 30.1958L18.9999 21.2389Z"
                  fill="#011C25"
                />
              </g>
              <defs>
                <clipPath id="clip0_494_4486">
                  <rect width="38" height="38" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
          <div className="content">
            <p>
              Positive Money is an international organization working to redesign our economic system for social justice
              and a liveable planet. Please select your preferred region below to see what we are doing near you.
            </p>
            <div className="regions">
              <div
                className="item"
                onClick={() => {
                  handleOnChangeRegion('UK');
                  handleOnClosePopup();
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  className="region_icon"
                >
                  <mask
                    // id="mask0_1707_2263"
                    x="2"
                    y="2"
                    width="56"
                    height="56"
                  >
                    <path
                      d="M58 30C58 37.4261 55.05 44.548 49.799 49.799C44.548 55.05 37.4261 58 30 58C22.5739 58 15.452 55.05 10.201 49.799C4.94999 44.548 2 37.4261 2 30C2 22.5739 4.94999 15.452 10.201 10.201C15.452 4.94999 22.5739 2 30 2C37.4261 2 44.548 4.94999 49.799 10.201C55.05 15.452 58 22.5739 58 30Z"
                      fill="white"
                    />
                  </mask>
                  <g
                  // mask="url(#mask0_1707_2263)"
                  >
                    <path d="M0 59.9993H60V0H0V59.9993Z" fill="#356BA0" />
                    <path d="M60 21.003H36V0H24V21.003H0V39.0035H24V59.9993H36V39.0035H60V21.003Z" fill="white" />
                    <path d="M60 53.1101L6.88487 0H0V6.94892L53.0625 59.9993H60V53.1101Z" fill="white" />
                    <path d="M0 53.1101L53.1141 0H60V6.94892L6.93744 59.9993H0V53.1101Z" fill="white" />
                    <path
                      d="M60 24.7507H33.5004V0H26.4996V24.7507H0V35.2486H26.4996V59.9993H33.5004V35.2486H60V24.7507Z"
                      fill="#CA4638"
                    />
                    <path d="M60.0001 59.8289V55.0861L43.8739 39.0034H39.123L60.0001 59.8289Z" fill="#CA4638" />
                    <path d="M60 0H55.2564L36 19.2116V21.003H38.9568L60 0.00540394" fill="#CA4638" />
                    <path d="M20.8842 39.0034L0 59.8343V59.9999H4.58327L24 40.6282V39.0041H20.8842" fill="#CA4638" />
                    <path d="M0 0.0407715V4.77995L16.26 21.0031H21.0105L0 0.0407715Z" fill="#CA4638" />
                  </g>
                </svg>
                <h4>PositiveMoney</h4>
                <h5>UK</h5>
              </div>

              <div
                className="item"
                onClick={() => {
                  handleOnChangeRegion('EU');
                  handleOnClosePopup();
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  className="region_icon"
                >
                  <path
                    d="M58 30.125C58 45.658 45.464 58.25 30 58.25C14.536 58.25 2 45.658 2 30.125C2 14.592 14.536 2 30 2C45.464 2 58 14.592 58 30.125Z"
                    fill="#2980B9"
                  />
                  <path
                    d="M29.9984 12.9656L30.5023 14.5109H32.1287L30.8133 15.4678L31.3138 17.0131L29.9984 16.0595L28.6831 17.0131L29.1835 15.4678L27.8682 14.5109H29.498L29.9984 12.9656Z"
                    fill="#F1C40F"
                  />
                  <path
                    d="M22.4037 14.9878L22.9075 16.5365L24.534 16.5338L23.2186 17.4907L23.7224 19.0394L22.4071 18.0858L21.0883 19.0427L21.5922 17.4941L20.2734 16.5371H21.9032L22.4037 14.9885"
                    fill="#F1C40F"
                  />
                  <path
                    d="M16.8481 20.5366L17.3519 22.0819H18.9817L17.663 23.0388L18.1702 24.5875L16.8515 23.6306L15.5361 24.5909L16.0366 23.0422L14.7212 22.0853H16.3476L16.8481 20.5366Z"
                    fill="#F1C40F"
                  />
                  <path
                    d="M37.5761 15.0115L38.0833 16.5601L39.7097 16.5574L38.3978 17.5178L38.9016 19.0664L37.5829 18.1095L36.2675 19.0664L36.7713 17.5211L35.4492 16.5642L37.079 16.5615L37.5761 15.0129"
                    fill="#F1C40F"
                  />
                  <path
                    d="M45.0355 21.9669L43.7201 22.9238L44.2239 24.4725L42.9086 23.5155L41.5898 24.4725L42.0936 22.9238L40.7749 21.9703H42.4014L42.9052 20.4182L43.4056 21.9669H45.0355Z"
                    fill="#F1C40F"
                  />
                  <path
                    d="M47.1316 29.5851L45.8095 30.5353L46.3066 32.0839L44.9946 31.1236L43.6725 32.0738L44.1797 30.5285L42.8677 29.5682L44.4941 29.5736L45.0047 28.0283L45.5018 29.577L47.1316 29.5824"
                    fill="#F1C40F"
                  />
                  <path
                    d="M44.3489 39.3979L43.0335 38.4443L41.7148 39.4012L42.2186 37.8526L40.8999 36.899L42.5263 36.8963L43.0301 35.3477L43.534 36.8963L45.1638 36.8936L43.8451 37.8505L44.3489 39.3992"
                    fill="#F1C40F"
                  />
                  <path
                    d="M28.8183 47.0328L29.3154 45.4842L28 44.5306L29.6264 44.5279L30.1269 42.9792L30.6341 44.5246L32.2571 44.5218L30.9452 45.4822L31.4524 47.0274L30.1336 46.0773L28.8183 47.0342"
                    fill="#F1C40F"
                  />
                  <path
                    d="M21.2133 45.0211L21.7137 43.4758L20.395 42.5188H22.0248L22.5253 40.9668L23.0291 42.5155L24.6555 42.5128L23.3402 43.4697L23.8474 45.0183L22.5287 44.0648L21.2133 45.0219"
                    fill="#F1C40F"
                  />
                  <path
                    d="M38.7967 44.9872L37.4814 44.0235L36.1593 44.9771L36.6699 43.4284L35.3579 42.4681L36.9843 42.4735L37.4916 40.9282L37.9886 42.4769L39.6184 42.4796L38.2963 43.433L38.7967 44.985"
                    fill="#F1C40F"
                  />
                  <path
                    d="M12.8682 29.6155L14.4946 29.6128L14.995 28.0608L15.5022 29.6061L17.1287 29.6034L15.8167 30.5671L16.3239 32.1124L15.0052 31.1588L13.6865 32.1191L14.1869 30.5704L12.8682 29.6155Z"
                    fill="#F1C40F"
                  />
                  <path
                    d="M15.6547 39.391L16.1551 37.8423L14.8364 36.8888L16.4662 36.8861L16.9701 35.3374L17.4739 36.8861H19.1003L17.785 37.843L18.2888 39.3883L16.9701 38.4347L15.6547 39.3917"
                    fill="#F1C40F"
                  />
                </svg>
                <h4>PositiveMoney</h4>
                <h5>EU</h5>
              </div>
              <div
                className="item"
                onClick={() => {
                  handleOnChangeRegion('GLOBAL');
                  handleOnClosePopup();
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  className="region_icon"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.02192 31.0962C1.41553 15.6518 13.4641 2.62365 28.9038 2.02192C44.3482 1.41553 57.3764 13.4641 57.9781 28.9038C58.5845 44.3482 46.5359 57.3764 31.0962 57.9781C15.6518 58.5845 2.62365 46.5359 2.02192 31.0962ZM31.0309 56.2895C32.8734 56.2009 34.6646 55.8697 36.3858 55.3286C36.7216 55.1793 37.0295 55.0161 37.2954 54.8388C32.6681 48.579 26.9494 51.8815 21.9723 53.5234C23.4276 55.3566 26.6835 56.2195 29.9114 56.3268C30.2892 56.3268 30.6577 56.3082 31.0309 56.2895ZM43.9097 37.8598C44.6794 36.9222 47.2029 36.2085 47.2636 35.0471C47.2916 34.5899 47.4362 33.9089 47.4921 33.4704C47.5341 33.06 44.8053 31.8145 44.3622 31.4414C43.0468 30.3685 42.641 27.7563 41.1157 26.5995C41.0877 26.5855 41.1157 26.5296 41.1157 26.4922C39.9122 26.343 35.1077 25.5267 34.366 24.3372C34.2308 24.1273 34.2261 23.9127 34.394 23.6748C33.769 23.4323 33.4145 23.241 32.7194 22.9985C28.5073 21.5291 25.0369 24.4398 23.3157 27.1639C20.1298 32.2017 29.8228 33.1579 31.0355 34.0349C32.3043 34.9444 32.9247 41.195 33.046 43.3733C33.7037 55.0534 42.7949 39.1939 43.9097 37.8598ZM12.0321 33.9089C11.9201 33.2699 9.252 31.6093 8.86018 30.9702C7.46547 28.6893 5.04456 24.3046 6.09409 21.767C4.63408 23.395 3.72449 26.8374 3.68251 30.5038C3.68251 30.6437 3.68717 30.779 3.69184 30.9143C3.81312 33.8156 4.39152 36.591 5.36175 39.1985C6.21537 40.7845 7.39084 42.0159 8.96746 42.5897C6.69115 40.2294 12.9184 38.6528 12.0321 33.9089ZM56.2755 28.3301C56.0236 24.3186 54.8808 20.5636 53.0476 17.2611C50.4961 14.0005 46.8064 10.1756 45.3837 10.1149C43.8398 10.045 42.2258 11.5703 41.3815 11.5376C40.7752 11.5097 41.167 9.98434 39.7489 9.81641C39.1332 9.73245 35.4902 12.3026 35.2057 12.7271C34.7532 13.3801 35.5229 14.3364 35.5229 15.204C35.5229 15.2926 35.3176 15.7358 35.3176 15.7358C32.962 16.72 28.3907 17.6762 29.235 20.2044C29.2863 20.335 29.333 20.461 29.4029 20.6009C30.0606 20.363 31.9638 19.2668 32.4909 19.3041C34.4733 19.4534 38.3542 21.3192 39.8656 22.3268C41.778 23.5909 44.6467 30.2146 48.5323 29.0064C49.3346 28.7639 49.4746 27.8823 49.0734 27.1779C48.6116 26.399 49.969 26.4456 50.1556 26.6368C50.6174 27.108 54.1018 30.0373 54.7642 30.4338C55.6738 30.9842 56.1262 29.9347 56.2755 28.3301ZM11.9435 14.8775C15.1667 13.2635 14.9661 15.12 17.0138 14.6489C18.138 14.383 18.0167 13.0536 18.2266 12.2C18.4972 11.1225 23.3577 8.78088 24.5098 8.24912C25.1628 7.94126 25.7972 7.60541 26.385 7.43282C28.6846 6.7378 21.5385 11.6169 21.4265 12.312C21.2819 13.1376 21.9303 13.3988 22.6346 13.2495C24.6171 12.8484 31.2688 9.95169 33.4331 9.05609C33.8343 7.72669 32.7334 7.12962 31.6746 7.32087C31.0635 7.41883 30.723 5.58099 30.9422 5.12386C29.9114 4.41951 28.7406 3.99503 27.4905 3.78046C20.2697 4.47082 13.9352 8.05787 9.6205 13.3288C9.96568 13.7953 10.4228 14.2338 10.2782 14.7469C10.4974 14.7655 11.7522 14.9801 11.9435 14.8775ZM10.2782 14.7469C10.1523 14.7375 10.3668 14.9101 10.2782 14.7469V14.7469Z"
                    fill="#1EB48F"
                  />
                </svg>
                <h4>PositiveMoney</h4>
                <h5>Global</h5>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default RegionSelector;
